import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Check, X, Trash2, Eye, RefreshCw, LogOut, CheckCircle2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { exitManagementAPI, employeeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { confirmToast } from '../lib/confirmToast';

const EXIT_TYPES = [
  'resignation', 'termination', 'retirement', 'end_of_contract', 'absconding', 'layoff', 'death',
];
const STATUSES = ['initiated', 'approved', 'clearance_pending', 'cleared', 'completed', 'rejected', 'cancelled'];
const CLEARANCE_AREAS = ['it', 'finance', 'hr', 'admin', 'reporting_manager'];
const FNF_STATUSES = ['pending', 'in_progress', 'processed', 'paid'];
const APPROVER_ROLES = ['workspace_admin', 'admin', 'hr_admin'];

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusVariant = (s) => ({
  initiated: 'secondary', approved: 'default', clearance_pending: 'secondary',
  cleared: 'default', completed: 'default', rejected: 'destructive', cancelled: 'outline',
}[s] || 'secondary');

const PAGE_SIZE = 25;

export default function ExitManagement() {
  const { user } = useAuth();
  const canApprove = APPROVER_ROLES.includes(user?.role);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: '', exit_type: 'resignation', reason: '', notice_period_days: 30, last_working_date: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.exit_type = typeFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await exitManagementAPI.list(params);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load exit cases');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, search]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await exitManagementAPI.summary();
      setSummary(data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openCreate = async () => {
    setForm({ employee_id: '', exit_type: 'resignation', reason: '', notice_period_days: 30, last_working_date: '' });
    setCreateOpen(true);
    if (employees.length === 0) {
      try {
        const { data } = await employeeAPI.list();
        setEmployees((Array.isArray(data) ? data : data.data || []).filter((e) => e.is_active !== false));
      } catch (e) { /* ignore */ }
    }
  };

  const submitCreate = async () => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    try {
      await exitManagementAPI.create({
        employee_id: form.employee_id,
        exit_type: form.exit_type,
        reason: form.reason || null,
        notice_period_days: form.notice_period_days ? Number(form.notice_period_days) : null,
        last_working_date: form.last_working_date || null,
      });
      toast.success('Exit case initiated');
      setCreateOpen(false);
      load(); loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to initiate exit');
    }
  };

  const refreshDetail = async (id) => {
    try {
      const { data } = await exitManagementAPI.get(id);
      setDetail(data);
    } catch (e) { toast.error('Failed to load case'); }
  };

  const doApprove = (row) => confirmToast({
    message: `Approve exit for ${row.employee_name || row.employee_id}?`,
    description: 'Moves the case to clearance.',
    confirmLabel: 'Approve',
    onConfirm: async () => {
      try { await exitManagementAPI.approve(row.id); toast.success('Exit approved'); load(); loadSummary(); if (detail?.id === row.id) refreshDetail(row.id); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    },
  });

  const doReject = (row) => confirmToast({
    message: `Reject exit for ${row.employee_name || row.employee_id}?`,
    confirmLabel: 'Reject',
    destructive: true,
    onConfirm: async () => {
      try { await exitManagementAPI.reject(row.id, 'Rejected'); toast.success('Exit rejected'); load(); loadSummary(); if (detail?.id === row.id) refreshDetail(row.id); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    },
  });

  const doComplete = (row) => confirmToast({
    message: `Complete offboarding for ${row.employee_name || row.employee_id}?`,
    description: 'This deactivates the employee on their last working date. This cannot be undone.',
    confirmLabel: 'Complete Exit',
    destructive: true,
    onConfirm: async () => {
      try { await exitManagementAPI.complete(row.id); toast.success('Offboarding completed'); load(); loadSummary(); if (detail?.id === row.id) refreshDetail(row.id); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Complete failed'); }
    },
  });

  const doCancel = (row) => confirmToast({
    message: `Cancel exit case for ${row.employee_name || row.employee_id}?`,
    confirmLabel: 'Cancel Case',
    destructive: true,
    onConfirm: async () => {
      try { await exitManagementAPI.remove(row.id); toast.success('Exit case cancelled'); load(); loadSummary(); setDetail(null); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
    },
  });

  const toggleClearance = async (area, cleared) => {
    if (!detail) return;
    try {
      const { data } = await exitManagementAPI.clearance(detail.id, area, cleared);
      setDetail(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Clearance update failed'); }
  };

  const saveDetailField = async (patch) => {
    if (!detail) return;
    try {
      const { data } = await exitManagementAPI.update(detail.id, patch);
      setDetail(data);
      load();
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clearanceCount = (c) => (c ? Object.values(c).filter(Boolean).length : 0);

  const statCards = useMemo(() => ([
    { label: 'Open Cases', value: summary?.open ?? '—' },
    { label: 'Completed', value: summary?.completed ?? '—' },
    { label: 'Total', value: summary?.total ?? '—' },
    { label: 'Resignations', value: summary?.by_type?.resignation ?? 0 },
  ]), [summary]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><LogOut className="h-5 w-5" /> Exit Management</h2>
          <p className="text-sm text-muted-foreground">Employee offboarding — resignation, clearance, exit interview and full &amp; final settlement.</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Initiate Exit</Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {statCards.map((s) => (
          <Card key={s.label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="text-2xl font-semibold">{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter || '__all__'} onValueChange={(v) => { setStatusFilter(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Type</Label>
          <Select value={typeFilter || '__all__'} onValueChange={(v) => { setTypeFilter(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Types</SelectItem>
              {EXIT_TYPES.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <Label className="text-xs">Search</Label>
          <Input className="h-9" placeholder="Name or Employee ID" value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setPage(1), load())} />
        </div>
        <Button variant="outline" size="sm" className="h-9" onClick={() => { setPage(1); load(); loadSummary(); }}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>LWD</TableHead>
              <TableHead>Clearance</TableHead>
              <TableHead>FnF</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No exit cases.</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="font-medium">{row.employee_name || row.employee_id}</div>
                  <div className="text-xs text-muted-foreground">{row.employee_id}{row.department ? ` · ${row.department}` : ''}</div>
                </TableCell>
                <TableCell>{titleize(row.exit_type)}</TableCell>
                <TableCell><Badge variant={statusVariant(row.status)}>{titleize(row.status)}</Badge></TableCell>
                <TableCell>{row.last_working_date || '—'}</TableCell>
                <TableCell>{clearanceCount(row.clearance)}/{CLEARANCE_AREAS.length}</TableCell>
                <TableCell>{titleize(row.fnf_status)}</TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => refreshDetail(row.id)}><Eye className="h-3.5 w-3.5" /></Button>
                  {canApprove && row.status === 'initiated' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => doApprove(row)}><Check className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => doReject(row)}><X className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                  {canApprove && ['cleared', 'clearance_pending'].includes(row.status) && (
                    <Button size="sm" variant="outline" onClick={() => doComplete(row)} title="Complete offboarding">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {row.status !== 'completed' && (
                    <Button size="sm" variant="outline" onClick={() => doCancel(row)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} case{total === 1 ? '' : 's'} · Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Initiate Exit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>
                      {(e.first_name || '') + ' ' + (e.last_name || '')} ({e.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Exit Type</Label>
                <Select value={form.exit_type} onValueChange={(v) => setForm((f) => ({ ...f, exit_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXIT_TYPES.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notice Period (days)</Label>
                <Input type="number" value={form.notice_period_days}
                  onChange={(e) => setForm((f) => ({ ...f, notice_period_days: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Last Working Date</Label>
              <Input type="date" value={form.last_working_date}
                onChange={(e) => setForm((f) => ({ ...f, last_working_date: e.target.value }))} />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate}>Initiate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {detail?.employee_name || detail?.employee_id} — {titleize(detail?.exit_type)}
              {detail && <Badge className="ml-2" variant={statusVariant(detail.status)}>{titleize(detail.status)}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Resigned On:</span> {detail.resignation_date || '—'}</div>
                <div><span className="text-muted-foreground">Last Working Day:</span> {detail.last_working_date || '—'}</div>
                <div><span className="text-muted-foreground">Notice:</span> {detail.notice_period_days ?? '—'} days</div>
                <div><span className="text-muted-foreground">Approved By:</span> {detail.approved_by || '—'}</div>
              </div>
              {detail.reason && <p className="text-sm"><span className="text-muted-foreground">Reason:</span> {detail.reason}</p>}

              {/* Clearance checklist */}
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Clearance</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {CLEARANCE_AREAS.map((area) => {
                    const done = detail.clearance?.[area];
                    const editable = !['completed', 'cancelled', 'rejected'].includes(detail.status);
                    return (
                      <button key={area} disabled={!editable}
                        onClick={() => toggleClearance(area, !done)}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded border text-sm transition-colors ${
                          done ? 'bg-green-50 border-green-300 text-green-800' : 'hover:bg-muted'} ${editable ? '' : 'opacity-70'}`}>
                        {done ? <Check className="h-4 w-4" /> : <X className="h-4 w-4 text-muted-foreground" />}
                        {titleize(area)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Exit interview + FnF quick edit */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Exit Interview</Label>
                  <Select value={detail.exit_interview_status || 'pending'}
                    onValueChange={(v) => saveDetailField({ exit_interview_status: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['pending', 'scheduled', 'completed', 'skipped'].map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">FnF Status</Label>
                  <Select value={detail.fnf_status || 'pending'}
                    onValueChange={(v) => saveDetailField({ fnf_status: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FNF_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {detail && canApprove && detail.status === 'initiated' && (
              <>
                <Button variant="outline" onClick={() => doReject(detail)}>Reject</Button>
                <Button onClick={() => doApprove(detail)}>Approve</Button>
              </>
            )}
            {detail && canApprove && ['cleared', 'clearance_pending'].includes(detail.status) && (
              <Button onClick={() => doComplete(detail)}>Complete Offboarding</Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
